import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { assetUrl } from '../lib/assetUrl';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Building2, Users, Plus, Trash2, Edit2, UserPlus, Save, Upload, Palette, Settings, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';

export const SystemAdmin = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyDialog, setCompanyDialog] = useState(false);
  const [settingsMode, setSettingsMode] = useState(false);
  const [companySettings, setCompanySettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [userDialog, setUserDialog] = useState(false);
  const [deleteCompanyId, setDeleteCompanyId] = useState(null);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [editCompany, setEditCompany] = useState(null);
  const [newCompany, setNewCompany] = useState({ name: '', contact_email: '', contact_phone: '' });
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'admin' });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('companies');
  const [siteSettings, setSiteSettings] = useState(null);
  const [savingSiteSettings, setSavingSiteSettings] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- load once on mount
  useEffect(() => { fetchCompanies(); }, []);
  useEffect(() => {
    if (selectedCompany) {
      fetchCompanyUsers(selectedCompany.id);
      fetchCompanySettings(selectedCompany.id);
    }
  }, [selectedCompany]);

  const fetchCompanies = async () => {
    try {
      const res = await axios.get(`${API}/companies`);
      setCompanies(res.data);
      if (res.data.length > 0 && !selectedCompany) setSelectedCompany(res.data[0]);
    } catch { toast.error('Kunne ikke hente selskaper'); }
    finally { setLoading(false); }
  };

  const fetchCompanyUsers = async (companyId) => {
    try {
      const res = await axios.get(`${API}/companies/${companyId}/users`);
      setCompanyUsers(res.data);
    } catch { toast.error('Kunne ikke hente brukere'); }
  };

  const fetchCompanySettings = async (companyId) => {
    try {
      const res = await axios.get(`${API}/company-settings/${companyId}`);
      setCompanySettings(res.data);
    } catch { setCompanySettings(null); }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    if (!newCompany.name.trim()) { toast.error('Selskapsnavn er påkrevd'); return; }
    try {
      await axios.post(`${API}/companies`, newCompany);
      toast.success('Selskap opprettet!');
      setCompanyDialog(false);
      setNewCompany({ name: '', contact_email: '', contact_phone: '' });
      fetchCompanies();
    } catch { toast.error('Kunne ikke opprette selskap'); }
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    if (!editCompany) return;
    try {
      await axios.put(`${API}/companies/${editCompany.id}`, {
        name: editCompany.name, contact_email: editCompany.contact_email, contact_phone: editCompany.contact_phone
      });
      toast.success('Selskap oppdatert!');
      setEditCompany(null);
      fetchCompanies();
    } catch { toast.error('Kunne ikke oppdatere selskap'); }
  };

  const handleDeleteCompany = async () => {
    if (!deleteCompanyId) return;
    try {
      await axios.delete(`${API}/companies/${deleteCompanyId}`);
      toast.success('Selskap slettet');
      setDeleteCompanyId(null);
      if (selectedCompany?.id === deleteCompanyId) { setSelectedCompany(null); setCompanyUsers([]); }
      fetchCompanies();
    } catch { toast.error('Kunne ikke slette selskap'); }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!selectedCompany || !newUser.name.trim() || !newUser.email.trim() || !newUser.password.trim()) {
      toast.error('Fyll ut alle felt'); return;
    }
    try {
      await axios.post(`${API}/companies/${selectedCompany.id}/users`, newUser);
      toast.success('Bruker opprettet!');
      setUserDialog(false);
      setNewUser({ name: '', email: '', password: '', role: 'admin' });
      fetchCompanyUsers(selectedCompany.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Kunne ikke opprette bruker'); }
  };

  const handleDeleteUser = async () => {
    if (!deleteUserId || !selectedCompany) return;
    try {
      await axios.delete(`${API}/companies/${selectedCompany.id}/users/${deleteUserId}`);
      toast.success('Bruker slettet');
      setDeleteUserId(null);
      fetchCompanyUsers(selectedCompany.id);
    } catch { toast.error('Kunne ikke slette bruker'); }
  };

  const handleSaveSettings = async () => {
    if (!selectedCompany || !companySettings) return;
    setSavingSettings(true);
    try {
      const res = await axios.put(`${API}/company-settings/${selectedCompany.id}`, companySettings);
      setCompanySettings(res.data);
      toast.success('Innstillinger lagret!');
      fetchCompanies();
    } catch { toast.error('Kunne ikke lagre innstillinger'); }
    finally { setSavingSettings(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedCompany) return;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/company-settings/${selectedCompany.id}/logo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCompanySettings(prev => ({ ...prev, logo_url: res.data.logo_url }));
      toast.success('Logo lastet opp!');
    } catch { toast.error('Kunne ikke laste opp logo'); }
    finally { setUploadingLogo(false); }
  };

  const updateSetting = (field, value) => setCompanySettings(prev => ({ ...prev, [field]: value }));

  const fetchSiteSettings = async () => {
    try {
      const res = await axios.get(`${API}/site-settings`);
      setSiteSettings(res.data);
    } catch { setSiteSettings(null); }
  };

  const handleSaveSiteSettings = async () => {
    if (!siteSettings) return;
    setSavingSiteSettings(true);
    try {
      const res = await axios.put(`${API}/site-settings`, siteSettings);
      setSiteSettings(res.data);
      toast.success('Startsideinnstillinger lagret!');
    } catch { toast.error('Kunne ikke lagre innstillinger'); }
    finally { setSavingSiteSettings(false); }
  };

  const updateSiteSetting = (field, value) => setSiteSettings(prev => ({ ...prev, [field]: value }));

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try { return format(new Date(dateStr), 'dd. MMM yyyy', { locale: nb }); } catch { return dateStr; }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="text-lg text-slate-600">Laster...</div></div>;

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Systemadministrasjon</h1>
        <p className="text-slate-600">Administrer selskaper, brukere og innstillinger</p>
      </div>

      {/* Top-level tabs */}
      <div className="flex gap-2 border-b border-slate-200 pb-2 mb-6">
        <Button variant={activeTab === 'companies' ? 'default' : 'ghost'} size="sm" onClick={() => setActiveTab('companies')} className={activeTab === 'companies' ? 'bg-slate-900' : ''} data-testid="tab-companies">
          <Building2 className="h-4 w-4 mr-1" /> Selskaper
        </Button>
        <Button variant={activeTab === 'homepage' ? 'default' : 'ghost'} size="sm" onClick={() => { setActiveTab('homepage'); if (!siteSettings) fetchSiteSettings(); }} className={activeTab === 'homepage' ? 'bg-slate-900' : ''} data-testid="tab-homepage">
          <Globe className="h-4 w-4 mr-1" /> Startside
        </Button>
      </div>

      {activeTab === 'homepage' ? (
        /* STARTSIDE TAB */
        siteSettings ? (
          <div className="max-w-2xl space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Sidetitler</CardTitle>
                <CardDescription>Tekst som vises øverst på innloggingssiden</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2"><Label>Sidetittel</Label><Input value={siteSettings.site_title || ''} onChange={(e) => updateSiteSetting('site_title', e.target.value)} placeholder="TapService Pro" data-testid="site-title-input" /></div>
                <div className="space-y-2"><Label>Undertittel</Label><Input value={siteSettings.site_subtitle || ''} onChange={(e) => updateSiteSetting('site_subtitle', e.target.value)} placeholder="Service av øl tappetårn" data-testid="site-subtitle-input" /></div>
                <div className="space-y-2"><Label>Beskrivelse (vises under innloggingskortet)</Label><Textarea value={siteSettings.site_description || ''} onChange={(e) => updateSiteSetting('site_description', e.target.value)} placeholder="Kort beskrivelse av systemet..." rows={3} data-testid="site-description-input" /></div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Innloggingskort</CardTitle>
                <CardDescription>Tekst på selve innloggingskortet</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2"><Label>Korttittel</Label><Input value={siteSettings.login_card_title || ''} onChange={(e) => updateSiteSetting('login_card_title', e.target.value)} placeholder="Logg inn" data-testid="login-card-title-input" /></div>
                <div className="space-y-2"><Label>Kortundertittel</Label><Input value={siteSettings.login_card_subtitle || ''} onChange={(e) => updateSiteSetting('login_card_subtitle', e.target.value)} placeholder="Skriv inn dine påloggingsdetaljer" data-testid="login-card-subtitle-input" /></div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Firmainformasjon i bunntekst</CardTitle>
                <CardDescription>Vises i bunnen av innloggingssiden</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2"><Label>Firmanavn</Label><Input value={siteSettings.footer_company_name || ''} onChange={(e) => updateSiteSetting('footer_company_name', e.target.value)} placeholder="Firma AS" data-testid="footer-company-input" /></div>
                <div className="space-y-2"><Label>Org.nummer</Label><Input value={siteSettings.footer_org_number || ''} onChange={(e) => updateSiteSetting('footer_org_number', e.target.value)} placeholder="Org nr. 123 456 789 MVA" data-testid="footer-org-input" /></div>
                <div className="space-y-2"><Label>E-post</Label><Input type="email" value={siteSettings.footer_email || ''} onChange={(e) => updateSiteSetting('footer_email', e.target.value)} placeholder="post@firma.no" data-testid="footer-email-input" /></div>
                <div className="space-y-2"><Label>Telefon</Label><Input value={siteSettings.footer_phone || ''} onChange={(e) => updateSiteSetting('footer_phone', e.target.value)} placeholder="+47 12 34 56 78" data-testid="footer-phone-input" /></div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Utviklerkontakt</CardTitle>
                <CardDescription>Vises diskret i bunnen av innloggingssiden</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2"><Label>Utviklernavn</Label><Input value={siteSettings.developer_name || ''} onChange={(e) => updateSiteSetting('developer_name', e.target.value)} placeholder="Navn på utvikler" data-testid="dev-name-input" /></div>
                <div className="space-y-2"><Label>E-post</Label><Input type="email" value={siteSettings.developer_email || ''} onChange={(e) => updateSiteSetting('developer_email', e.target.value)} placeholder="utvikler@firma.no" data-testid="dev-email-input" /></div>
                <div className="space-y-2"><Label>Telefon</Label><Input value={siteSettings.developer_phone || ''} onChange={(e) => updateSiteSetting('developer_phone', e.target.value)} placeholder="+47 12 34 56 78" data-testid="dev-phone-input" /></div>
                <div className="space-y-2"><Label>Nettside</Label><Input value={siteSettings.developer_website || ''} onChange={(e) => updateSiteSetting('developer_website', e.target.value)} placeholder="https://utvikler.no" data-testid="dev-website-input" /></div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Bakgrunnsbilde</CardTitle>
                <CardDescription>URL til bakgrunnsbildet på innloggingssiden (la stå tomt for standard)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2"><Input value={siteSettings.background_image_url || ''} onChange={(e) => updateSiteSetting('background_image_url', e.target.value)} placeholder="https://eksempel.no/bilde.jpg" data-testid="bg-image-input" /></div>
              </CardContent>
            </Card>

            <Button onClick={handleSaveSiteSettings} disabled={savingSiteSettings} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="save-site-settings-btn">
              <Save className="h-4 w-4 mr-2" />{savingSiteSettings ? 'Lagrer...' : 'Lagre startsideinnstillinger'}
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-center h-32 text-slate-500">Laster innstillinger...</div>
        )
      ) : (
      /* SELSKAPER TAB */
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Selskaper */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Building2 className="h-5 w-5 text-slate-600" /><CardTitle>Selskaper</CardTitle></div>
                <Dialog open={companyDialog} onOpenChange={setCompanyDialog}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-slate-900 hover:bg-slate-800" data-testid="add-company-btn"><Plus className="h-4 w-4" /></Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Nytt Selskap</DialogTitle><DialogDescription>Opprett et nytt selskap</DialogDescription></DialogHeader>
                    <form onSubmit={handleCreateCompany} className="space-y-4">
                      <div className="space-y-2"><Label>Selskapsnavn *</Label><Input value={newCompany.name} onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })} placeholder="Navn på selskap" data-testid="new-company-name" /></div>
                      <div className="space-y-2"><Label>Kontakt e-post</Label><Input type="email" value={newCompany.contact_email} onChange={(e) => setNewCompany({ ...newCompany, contact_email: e.target.value })} placeholder="kontakt@selskap.no" /></div>
                      <div className="space-y-2"><Label>Telefon</Label><Input value={newCompany.contact_phone} onChange={(e) => setNewCompany({ ...newCompany, contact_phone: e.target.value })} placeholder="+47 XXX XX XXX" /></div>
                      <div className="flex gap-2 pt-2">
                        <Button type="button" variant="outline" onClick={() => setCompanyDialog(false)} className="flex-1">Avbryt</Button>
                        <Button type="submit" className="flex-1 bg-slate-900 hover:bg-slate-800">Opprett</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              <CardDescription>{companies.length} selskaper</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {companies.map((company) => (
                  <div key={company.id} className={`p-3 rounded-lg border cursor-pointer transition-colors ${selectedCompany?.id === company.id ? 'bg-brand-50 border-brand-200' : 'bg-white border-slate-200 hover:bg-slate-50'}`} onClick={() => { setSelectedCompany(company); setSettingsMode(false); }} data-testid={`company-${company.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        {company.logo_url && <img src={assetUrl(company.logo_url)} alt="" className="h-6 w-6 object-contain rounded shrink-0" />}
                        <div className="min-w-0"><p className="font-medium text-slate-900 truncate">{company.company_name || company.name}</p>{company.contact_email && <p className="text-xs text-slate-500 truncate">{company.contact_email}</p>}</div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setEditCompany(company); }} className="h-7 w-7 p-0 text-slate-500"><Edit2 className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setDeleteCompanyId(company.id); }} className="h-7 w-7 p-0 text-red-500"><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </div>
                  </div>
                ))}
                {companies.length === 0 && <p className="text-center text-slate-500 py-4">Ingen selskaper ennå</p>}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Detaljer for valgt selskap */}
        <div className="lg:col-span-2">
          {selectedCompany ? (
            <div className="space-y-4">
              {/* Tabs: Brukere / Innstillinger */}
              <div className="flex gap-2 border-b border-slate-200 pb-2">
                <Button variant={!settingsMode ? 'default' : 'ghost'} size="sm" onClick={() => setSettingsMode(false)} className={!settingsMode ? 'bg-slate-900' : ''} data-testid="tab-users">
                  <Users className="h-4 w-4 mr-1" /> Brukere
                </Button>
                <Button variant={settingsMode ? 'default' : 'ghost'} size="sm" onClick={() => setSettingsMode(true)} className={settingsMode ? 'bg-slate-900' : ''} data-testid="tab-settings">
                  <Settings className="h-4 w-4 mr-1" /> Firmainnstillinger
                </Button>
              </div>

              {!settingsMode ? (
                /* BRUKERE */
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div><CardTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-slate-600" />Brukere i {selectedCompany.company_name || selectedCompany.name}</CardTitle><CardDescription>{companyUsers.length} brukere</CardDescription></div>
                      <Dialog open={userDialog} onOpenChange={setUserDialog}>
                        <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-user-btn"><UserPlus className="h-4 w-4 mr-2" />Ny Bruker</Button></DialogTrigger>
                        <DialogContent>
                          <DialogHeader><DialogTitle>Ny Bruker</DialogTitle><DialogDescription>Opprett bruker for {selectedCompany.company_name || selectedCompany.name}</DialogDescription></DialogHeader>
                          <form onSubmit={handleCreateUser} className="space-y-4">
                            <div className="space-y-2"><Label>Navn *</Label><Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} placeholder="Fullt navn" data-testid="new-user-name" /></div>
                            <div className="space-y-2"><Label>E-post *</Label><Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} placeholder="bruker@selskap.no" data-testid="new-user-email" /></div>
                            <div className="space-y-2"><Label>Passord *</Label><Input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder="Minimum 6 tegn" data-testid="new-user-password" /></div>
                            <div className="space-y-2">
                              <Label>Rolle</Label>
                              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="admin">Administrator</SelectItem>
                                  <SelectItem value="tekniker">Tekniker</SelectItem>
                                  <SelectItem value="lager">Lager</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="flex gap-2 pt-2"><Button type="button" variant="outline" onClick={() => setUserDialog(false)} className="flex-1">Avbryt</Button><Button type="submit" className="flex-1 bg-slate-900 hover:bg-slate-800">Opprett</Button></div>
                          </form>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {companyUsers.length === 0 ? (
                      <div className="text-center py-8"><Users className="h-12 w-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-600">Ingen brukere ennå</p></div>
                    ) : (
                      <div className="space-y-2">
                        {companyUsers.map((user) => (
                          <div key={user.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div>
                              <p className="font-medium text-slate-900">{user.name}</p>
                              <p className="text-sm text-slate-600">{user.email}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${user.role === 'admin' ? 'bg-brand-100 text-brand-700' : user.role === 'lager' ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-600'}`}>
                                  {user.role === 'admin' ? 'Administrator' : user.role === 'lager' ? 'Lager' : 'Tekniker'}
                                </span>
                                <span className="text-xs text-slate-400">Opprettet {formatDate(user.created_at)}</span>
                              </div>
                            </div>
                            <Button size="sm" variant="ghost" onClick={() => setDeleteUserId(user.id)} className="text-red-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                /* FIRMAINNSTILLINGER */
                companySettings && (
                  <div className="space-y-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2"><Building2 className="h-5 w-5 text-slate-600" />Firmainformasjon</CardTitle>
                        <CardDescription>Denne informasjonen vises i sidebar og på bekreftelsesdokumenter for dette selskapet</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2 sm:col-span-2"><Label>Firmanavn (vises i sidebar)</Label><Input value={companySettings.company_name || ''} onChange={(e) => updateSetting('company_name', e.target.value)} placeholder="Brewery AS" data-testid="setting-company-name" /></div>
                          <div className="space-y-2 sm:col-span-2"><Label>Adresse</Label><Input value={companySettings.address || ''} onChange={(e) => updateSetting('address', e.target.value)} placeholder="Gateadresse 1" data-testid="setting-address" /></div>
                          <div className="space-y-2"><Label>Postnummer</Label><Input value={companySettings.postal_code || ''} onChange={(e) => updateSetting('postal_code', e.target.value)} placeholder="0000" /></div>
                          <div className="space-y-2"><Label>Sted</Label><Input value={companySettings.city || ''} onChange={(e) => updateSetting('city', e.target.value)} placeholder="Oslo" /></div>
                          <div className="space-y-2"><Label>Org.nummer</Label><Input value={companySettings.org_number || ''} onChange={(e) => updateSetting('org_number', e.target.value)} placeholder="123 456 789" /></div>
                          <div className="space-y-2"><Label>Telefon</Label><Input value={companySettings.phone || ''} onChange={(e) => updateSetting('phone', e.target.value)} placeholder="+47 12 34 56 78" /></div>
                          <div className="space-y-2 sm:col-span-2"><Label>E-post</Label><Input type="email" value={companySettings.email || ''} onChange={(e) => updateSetting('email', e.target.value)} placeholder="post@firma.no" /></div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader><CardTitle className="flex items-center gap-2"><Palette className="h-5 w-5 text-slate-600" />Dokumentinnstillinger</CardTitle></CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-2">
                          <Label>Logo</Label>
                          <div className="flex items-center gap-4">
                            {companySettings.logo_url && <img src={assetUrl(companySettings.logo_url)} alt="Logo" className="h-12 object-contain border rounded p-1" />}
                            <input type="file" ref={logoInputRef} accept="image/*" onChange={handleLogoUpload} className="hidden" />
                            <Button type="button" variant="outline" size="sm" onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo} data-testid="upload-logo-btn">
                              <Upload className="h-4 w-4 mr-1" />{uploadingLogo ? 'Laster opp...' : 'Last opp logo'}
                            </Button>
                          </div>
                        </div>
                        <div className="space-y-2"><Label>Dokumentoverskrift</Label><Input value={companySettings.document_header || ''} onChange={(e) => updateSetting('document_header', e.target.value)} placeholder="Din partner for drikkevarer" /></div>
                        <div className="space-y-2"><Label>Tittel på monteringsskjema (PDF)</Label><Input data-testid="sysadmin-installation-title" value={companySettings.installation_title || ''} onChange={(e) => updateSetting('installation_title', e.target.value)} placeholder="MONTERINGSSKJEMA" maxLength={60} /></div>
                        <div className="space-y-2"><Label>Undertekst monteringsskjema (valgfri)</Label><Input data-testid="sysadmin-installation-subtitle" value={companySettings.installation_subtitle || ''} onChange={(e) => updateSetting('installation_subtitle', e.target.value)} maxLength={100} /></div>
                        <div className="space-y-2"><Label>Dokumentbunntekst</Label><Textarea value={companySettings.document_footer || ''} onChange={(e) => updateSetting('document_footer', e.target.value)} placeholder="F.eks. kontaktinfo eller vilkår" rows={2} /></div>
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2"><Palette className="h-4 w-4" />Dokumentfarge</Label>
                          <div className="flex items-center gap-3">
                            <input type="color" value={companySettings.primary_color || '#1e293b'} onChange={(e) => updateSetting('primary_color', e.target.value)} className="h-9 w-14 rounded border cursor-pointer" />
                            <span className="text-sm text-slate-500">{companySettings.primary_color || '#1e293b'}</span>
                          </div>
                        </div>
                        <Button onClick={handleSaveSettings} disabled={savingSettings} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="save-settings-btn">
                          <Save className="h-4 w-4 mr-2" />{savingSettings ? 'Lagrer...' : 'Lagre innstillinger'}
                        </Button>
                      </CardContent>
                    </Card>
                  </div>
                )
              )}
            </div>
          ) : (
            <Card><CardContent className="py-12 text-center"><Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-600">Velg et selskap for å se brukere og innstillinger</p></CardContent></Card>
          )}
        </div>
      </div>
      )}

      {/* Edit Company Dialog */}
      <Dialog open={!!editCompany} onOpenChange={() => setEditCompany(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Rediger Selskap</DialogTitle></DialogHeader>
          {editCompany && (
            <form onSubmit={handleUpdateCompany} className="space-y-4">
              <div className="space-y-2"><Label>Selskapsnavn *</Label><Input value={editCompany.name} onChange={(e) => setEditCompany({ ...editCompany, name: e.target.value })} /></div>
              <div className="space-y-2"><Label>Kontakt e-post</Label><Input type="email" value={editCompany.contact_email || ''} onChange={(e) => setEditCompany({ ...editCompany, contact_email: e.target.value })} /></div>
              <div className="space-y-2"><Label>Telefon</Label><Input value={editCompany.contact_phone || ''} onChange={(e) => setEditCompany({ ...editCompany, contact_phone: e.target.value })} /></div>
              <div className="flex gap-2 pt-2"><Button type="button" variant="outline" onClick={() => setEditCompany(null)} className="flex-1">Avbryt</Button><Button type="submit" className="flex-1 bg-slate-900 hover:bg-slate-800">Lagre</Button></div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Dialogs */}
      <AlertDialog open={!!deleteCompanyId} onOpenChange={() => setDeleteCompanyId(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Slett selskap?</AlertDialogTitle><AlertDialogDescription>Dette vil slette selskapet og alle tilhørende brukere.</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel>Avbryt</AlertDialogCancel><AlertDialogAction onClick={handleDeleteCompany} className="bg-red-600 hover:bg-red-700">Slett</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!deleteUserId} onOpenChange={() => setDeleteUserId(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Slett bruker?</AlertDialogTitle><AlertDialogDescription>Er du sikker på at du vil slette denne brukeren?</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel>Avbryt</AlertDialogCancel><AlertDialogAction onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700">Slett</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SystemAdmin;
